import { AuthContext } from '@/contexts/AuthContext';
import * as Sentry from '@sentry/react';
import { useDebounce } from '@superfunnel/react-hooks/useDebounce';
import { DynamicPage, DynamicPageTag } from '@superfunnel/types';
import AlertModal from '@superfunnel/ui-core/AlertModal';
import Button from '@superfunnel/ui-core/v2/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@superfunnel/ui-core/v2/DropdownMenu';
import Input from '@superfunnel/ui-core/v2/Input';
import Switch from '@superfunnel/ui-core/v2/Switch';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@superfunnel/ui-core/v2/Tooltip';
import { QueryDocumentSnapshot } from 'firebase/firestore';
import {
  ArrowUpRight,
  Copy,
  Filter,
  Import,
  Label,
  Link,
  MoreVert,
  OpenNewWindow,
  Search,
  Sparks,
} from 'iconoir-react';
import { Trash } from 'lucide-react';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import DashboardAnalyticsRangeSelector from '../components/DashboardAnalyticsRangeSelector';
import {
  DynamicPagesTable,
  DynamicPagesTableHandle,
} from '../components/DynamicPagesTable';
import { useAnalytics } from '../contexts/AnalyticsContext';
import { DashboardContext } from '../contexts/DashboardContext';
import { SnackbarContext } from '../contexts/SnackbarContext';
import { UserContext } from '../contexts/UserContext';
import CreateDynamicPageModal from '../modals/CreateDynamicPageModal';
import BulkPublishDynamicPagesModal from '../modals/DynamicPageUpdateTools/BulkPublishDynamicPagesModal';
import UpdateDynamicPagesLinksModal from '../modals/DynamicPageUpdateTools/UpdateDynamicPagesLinksModal';
import UpdateDynamicPagesTagsModal from '../modals/DynamicPageUpdateTools/UpdateDynamicPagesTagsModal';
import SubscriptionRequiredModal from '../modals/SubscriptionRequiredModal';
import DynamicPagesFilterPopover from '../popovers/DynamicPagesFilterPopover';
import ExportAnalyticsPopover from '../popovers/ExportAnalyticsPopover';
import {
  deleteDynamicPage,
  duplicateDynamicPage,
} from '../services/DynamicPagesService';

const DYNAMIC_PAGES_CONFIG = 'DYNAMIC_PAGES_CONFIG';

interface DynamicPageFilters {
  tags: DynamicPageTag[];
  statuses: (DynamicPage['status'] | 'inactive')[];
  changes: boolean[];
  domains: string[];
}

type PartialDynamicPageFilters = {
  tags?: string[];
  statuses?: DynamicPageFilters['statuses'];
  changes?: boolean[];
  domains?: string[];
};

function filterFromSearchParams(
  searchParams: URLSearchParams,
): PartialDynamicPageFilters {
  const filters: PartialDynamicPageFilters = {};

  const tags =
    searchParams.get('tags') ||
    localStorage.getItem(`${DYNAMIC_PAGES_CONFIG}:tags`);
  if (tags) {
    try {
      filters.tags = JSON.parse(tags);
    } catch (e) {
      Sentry.captureException(e);
      console.error('Error parsing tags from search params', e);
    }
  }

  const statuses =
    searchParams.get('statuses') ||
    localStorage.getItem(`${DYNAMIC_PAGES_CONFIG}:statuses`);
  if (statuses) {
    try {
      filters.statuses = JSON.parse(statuses);
    } catch (e) {
      Sentry.captureException(e);
      console.error('Error parsing statuses from search params', e);
    }
  }

  const changes =
    searchParams.get('changes') ||
    localStorage.getItem(`${DYNAMIC_PAGES_CONFIG}:changes`);
  if (changes) {
    try {
      filters.changes = JSON.parse(changes);
    } catch (e) {
      Sentry.captureException(e);
      console.error('Error parsing changes from search params', e);
    }
  }

  const domains =
    searchParams.get('domains') ||
    localStorage.getItem(`${DYNAMIC_PAGES_CONFIG}:domains`);
  if (domains) {
    try {
      filters.domains = JSON.parse(domains);
    } catch (e) {
      Sentry.captureException(e);
      console.error('Error parsing domains from search params', e);
    }
  }

  return filters;
}

export default function DynamicPages() {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const tableRef = useRef<DynamicPagesTableHandle>(null);
  const { track } = useAnalytics();

  const { show } = useContext(SnackbarContext);
  const { isSupportAdmin } = useContext(AuthContext);
  const { selectedAccount } = useContext(UserContext);
  const {
    dynamicPages,
    domains,
    analyticsType,
    setAnalyticsType,
    hasActiveSubscription,
  } = useContext(DashboardContext);
  // const { customRangeAnalytics } = useContext(OverviewContext);

  const [createDynamicPageDialogOpen, setCreateDynamicPageDialogOpen] =
    useState(false);
  const [duplicateDynamicPageModal, setDuplicateDynamicPageModal] = useState<{
    dynamicPage: QueryDocumentSnapshot<DynamicPage, DynamicPage>;
    loading?: boolean;
  }>();
  const [deleteDynamicPageModal, setDeleteDynamicPageModal] = useState<{
    dynamicPage: QueryDocumentSnapshot<DynamicPage, DynamicPage>;
    loading?: boolean;
  }>();
  const [enableRowSelection, _setEnableRowSelection] = useState(true);
  const [rowsSelected, setRowsSelected] = useState<Record<string, boolean>>({});
  const [updateDynamicPagesTagsModalOpen, setUpdateDynamicPagesTagsModalOpen] =
    useState(false);
  const [
    updateDynamicPagesLinksModalOpen,
    setUpdateDynamicPagesLinksModalOpen,
  ] = useState(false);
  const [
    bulkPublishDynamicPagesModalOpen,
    setBulkPublishDynamicPagesModalOpen,
  ] = useState(false);
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);

  const [searchQuery, setSearchQuery] = useState(
    (searchParams.get('search') ||
      localStorage.getItem(`${DYNAMIC_PAGES_CONFIG}:search`)) ??
      '',
  );
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [filters, setFilters] = useState<PartialDynamicPageFilters>(
    filterFromSearchParams(searchParams),
  );

  // Track search query changes
  useEffect(() => {
    if (debouncedSearchQuery) {
      track('dynamic_pages_searched', {
        query: debouncedSearchQuery,
        query_length: debouncedSearchQuery.length,
      });
    }
  }, [debouncedSearchQuery, track]);

  useEffect(() => {
    setSearchParams((params) => {
      if (debouncedSearchQuery) {
        params.set('search', debouncedSearchQuery);
        localStorage.setItem(
          `${DYNAMIC_PAGES_CONFIG}:search`,
          debouncedSearchQuery,
        );
      } else {
        params.delete('search');
        localStorage.removeItem(`${DYNAMIC_PAGES_CONFIG}:search`);
      }

      if (filters.tags?.length) {
        params.set('tags', JSON.stringify(filters.tags));
        localStorage.setItem(
          `${DYNAMIC_PAGES_CONFIG}:tags`,
          JSON.stringify(filters.tags),
        );
      } else {
        params.delete('tags');
        localStorage.removeItem(`${DYNAMIC_PAGES_CONFIG}:tags`);
      }

      if (filters.statuses?.length) {
        params.set('statuses', JSON.stringify(filters.statuses));
        localStorage.setItem(
          `${DYNAMIC_PAGES_CONFIG}:statuses`,
          JSON.stringify(filters.statuses),
        );
      } else {
        params.delete('statuses');
        localStorage.removeItem(`${DYNAMIC_PAGES_CONFIG}:statuses`);
      }

      if (filters.changes?.length) {
        params.set('changes', JSON.stringify(filters.changes));
        localStorage.setItem(
          `${DYNAMIC_PAGES_CONFIG}:changes`,
          JSON.stringify(filters.changes),
        );
      } else {
        params.delete('changes');
        localStorage.removeItem(`${DYNAMIC_PAGES_CONFIG}:changes`);
      }

      if (filters.domains?.length) {
        params.set('domains', JSON.stringify(filters.domains));
        localStorage.setItem(
          `${DYNAMIC_PAGES_CONFIG}:domains`,
          JSON.stringify(filters.domains),
        );
      } else {
        params.delete('domains');
        localStorage.removeItem(`${DYNAMIC_PAGES_CONFIG}:domains`);
      }

      return new URLSearchParams(params);
    });
  }, [debouncedSearchQuery, filters, setSearchParams]);

  const filteredDynamicPages = useMemo(() => {
    const filtered = dynamicPages?.filter((c) => {
      const campaignData = c.data();

      // Filter out quiz DynamicPages - they have their own QuizFunnels screen
      if (campaignData.type === 'quiz') {
        return false;
      }

      if (debouncedSearchQuery && debouncedSearchQuery.length > 2) {
        if (
          !campaignData.name
            .toLowerCase()
            .includes(debouncedSearchQuery.toLowerCase()) &&
          !campaignData.path
            .toLowerCase()
            .includes(debouncedSearchQuery.toLowerCase())
        ) {
          return false;
        }
      }

      if (filters.tags?.length) {
        if (
          !campaignData.tags?.some((t) =>
            filters.tags?.some((tag) => tag === t.id),
          )
        ) {
          return false;
        }
      }

      if (filters.statuses?.length) {
        const campaignStatus = campaignData.status;
        if (!filters.statuses?.includes(campaignStatus)) {
          return false;
        }
      }

      if (filters.changes?.length) {
        const hasChanges = campaignData.hasChangesToPublish;
        if (!filters.changes?.includes(hasChanges ?? false)) {
          return false;
        }
      }

      if (filters.domains?.length) {
        if (
          campaignData.domains?.length &&
          !campaignData.domains?.some((d) => filters.domains?.includes(d))
        ) {
          return false;
        }
      }

      return true;
    });

    return filtered;
  }, [dynamicPages, debouncedSearchQuery, filters]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.tags?.length) count++;
    if (filters.statuses?.length) count++;
    if (filters.changes?.length) count++;
    if (filters.domains?.length) count++;
    return count;
  }, [filters]);

  // Track filter changes
  useEffect(() => {
    if (
      filters.tags?.length ||
      filters.statuses?.length ||
      filters.changes?.length ||
      filters.domains?.length
    ) {
      track('dynamic_pages_filters_applied', {
        tags_count: filters.tags?.length || 0,
        statuses_count: filters.statuses?.length || 0,
        changes_filter: filters.changes?.length || 0,
        domains_count: filters.domains?.length || 0,
        total_filters: activeFilterCount,
      });
    }
  }, [filters, activeFilterCount, track]);

  const filterOptions = useMemo(() => {
    const tagsMap: Map<string, DynamicPageTag> = new Map();
    dynamicPages?.forEach((c) => {
      c.data().tags?.forEach((t) => {
        tagsMap.set(t.id, t);
      });
    });

    const _filters: DynamicPageFilters = {
      tags: Array.from(tagsMap.values()),
      statuses: ['active', 'inactive'],
      changes: [true, false],
      domains: domains?.map((d) => d.data().domain) || [],
    };

    return _filters;
  }, [dynamicPages, domains]);

  return (
    <>
      <CreateDynamicPageModal
        open={createDynamicPageDialogOpen}
        onClose={() => setCreateDynamicPageDialogOpen(false)}
      />
      <AlertModal
        type="info"
        title={`Are you sure you'd like to duplicate ${
          duplicateDynamicPageModal?.dynamicPage.data()?.name
        }?`}
        description="This will create a new dynamic page with all the same settings and variants."
        open={!!duplicateDynamicPageModal}
        onClose={() => {
          if (duplicateDynamicPageModal?.loading) return;
          setDuplicateDynamicPageModal(undefined);
        }}
        actions={[
          {
            title: 'Cancel',
            disabled: duplicateDynamicPageModal?.loading,
            type: 'secondary',
            onClick: () => {
              setDuplicateDynamicPageModal(undefined);
            },
          },
          {
            title: 'Duplicate',
            disabled: duplicateDynamicPageModal?.loading,
            type: 'primary',
            // loading: duplicateDynamicPageModal?.loading,
            onClick: async () => {
              const config = duplicateDynamicPageModal;
              if (!config || !selectedAccount) return;

              track('dynamic_page_duplicate_confirmed', {
                dynamic_page_id: config.dynamicPage.id,
                dynamic_page_name: config.dynamicPage.data().name,
              });

              setDuplicateDynamicPageModal({ ...config, loading: true });
              duplicateDynamicPage(selectedAccount.id, config.dynamicPage.id)
                .then(({ campaignId }) => {
                  track('dynamic_page_duplicate_success', {
                    original_id: config.dynamicPage.id,
                    new_id: campaignId,
                  });
                  setDuplicateDynamicPageModal(undefined);
                  navigate(`/workspace/${slug}/pages/${campaignId}`);
                })
                .catch((e) => {
                  track('dynamic_page_duplicate_failed', {
                    dynamic_page_id: config.dynamicPage.id,
                    error: e.message,
                  });
                  setDuplicateDynamicPageModal({ ...config, loading: false });
                  show('error duplicating dynamic page', e);
                  Sentry.captureException(e);
                });
            },
          },
        ]}
      />
      <AlertModal
        type="warn"
        title={`Are you sure you'd like to delete ${
          deleteDynamicPageModal?.dynamicPage.data()?.name
        }?`}
        description="This action cannot be undone. This will permanently delete the dynamic page."
        open={!!deleteDynamicPageModal}
        onClose={() => {
          if (deleteDynamicPageModal?.loading) return;
          setDeleteDynamicPageModal(undefined);
        }}
        actions={[
          {
            title: 'Cancel',
            disabled: deleteDynamicPageModal?.loading,
            type: 'secondary',
            onClick: () => {
              track('dynamic_page_delete_cancelled', {
                dynamic_page_id: deleteDynamicPageModal?.dynamicPage.id,
              });
              setDeleteDynamicPageModal(undefined);
            },
          },
          {
            title: 'Delete',
            disabled: deleteDynamicPageModal?.loading,
            type: 'primary',
            color: 'negative',
            onClick: async () => {
              const config = deleteDynamicPageModal;
              if (!config || !selectedAccount) return;

              track('dynamic_page_delete_confirmed', {
                dynamic_page_id: config.dynamicPage.id,
                dynamic_page_name: config.dynamicPage.data().name,
              });

              setDeleteDynamicPageModal({ ...config, loading: true });
              deleteDynamicPage(selectedAccount.id, config.dynamicPage.id)
                .then(() => {
                  track('dynamic_page_delete_success', {
                    dynamic_page_id: config.dynamicPage.id,
                  });
                  setDeleteDynamicPageModal(undefined);
                  show('Dynamic page deleted successfully');
                })
                .catch((e) => {
                  track('dynamic_page_delete_failed', {
                    dynamic_page_id: config.dynamicPage.id,
                    error: e.message,
                  });
                  setDeleteDynamicPageModal({ ...config, loading: false });
                  show('error deleting dynamic page', e);
                  Sentry.captureException(e);
                });
            },
          },
        ]}
      />
      <UpdateDynamicPagesTagsModal
        open={updateDynamicPagesTagsModalOpen}
        onClose={() => setUpdateDynamicPagesTagsModalOpen(false)}
        dynamicPages={
          Object.entries(rowsSelected)
            .map(([id, selected]) =>
              selected ? dynamicPages?.find((c) => c.id === id) : null,
            )
            .filter(Boolean) as QueryDocumentSnapshot<DynamicPage>[]
        }
      />
      <UpdateDynamicPagesLinksModal
        open={updateDynamicPagesLinksModalOpen}
        onClose={() => setUpdateDynamicPagesLinksModalOpen(false)}
        dynamicPages={
          Object.entries(rowsSelected)
            .map(([id, selected]) =>
              selected ? dynamicPages?.find((c) => c.id === id) : null,
            )
            .filter(Boolean) as QueryDocumentSnapshot<DynamicPage>[]
        }
      />
      <BulkPublishDynamicPagesModal
        open={bulkPublishDynamicPagesModalOpen}
        onClose={() => setBulkPublishDynamicPagesModalOpen(false)}
        dynamicPages={
          Object.entries(rowsSelected)
            .map(([id, selected]) =>
              selected ? dynamicPages?.find((c) => c.id === id) : null,
            )
            .filter(Boolean) as QueryDocumentSnapshot<DynamicPage>[]
        }
      />
      <div className="flex h-full flex-col px-3 py-3 lg:px-10 lg:py-6">
        <div className="flex flex-col items-stretch sm:flex-row sm:items-center">
          <div className="flex w-full flex-col items-center justify-between gap-2 lg:flex-row">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Dynamic Pages
              </h1>
              <p className="mt-1 text-sm text-gray-600">
                Create and manage dynamic pages for personalized user
                experiences.
              </p>
            </div>
            <div className="flex flex-row gap-2.5">
              <Input
                icon={<Search className="h-5 w-5" />}
                className=""
                value={searchQuery}
                onChange={(e: any) => {
                  setSearchQuery(e.target.value);
                }}
                placeholder="Search"
              />
              <div className="shrink-0">
                <DynamicPagesFilterPopover
                  filters={filters}
                  filterOptions={filterOptions}
                  onFiltersChange={(newFilters) => {
                    setFilters((currentFilters) => ({
                      ...currentFilters,
                      ...newFilters,
                    }));
                  }}
                  onReset={() => {
                    track('dynamic_pages_filters_reset', {
                      previous_filters_count: activeFilterCount,
                    });
                    setFilters({});
                  }}
                >
                  <Button
                    variant="outline"
                    color="white"
                    className="rounded-2lg relative h-full w-10"
                  >
                    {activeFilterCount > 0 && (
                      <div className="absolute top-0.5 right-0.5 rounded-md border-2 border-white bg-red-500 px-1 text-xs text-[10px] text-white">
                        {activeFilterCount}
                      </div>
                    )}
                    <Filter className="h-5 w-5" />
                  </Button>
                </DynamicPagesFilterPopover>
              </div>
              <ExportAnalyticsPopover>
                <Button
                  className={'rounded-2lg w-10'}
                  variant="outline"
                  color="white"
                  onClick={() => {
                    track('export_analytics_clicked', {
                      source: 'dynamic_pages',
                      filtered_pages_count: filteredDynamicPages?.length || 0,
                    });
                  }}
                >
                  <Import className="h-5 w-5" />
                </Button>
              </ExportAnalyticsPopover>
              <Button
                className="rounded-2lg font-inter h-10 shrink-0 gap-2.5 tracking-[0.01em] has-[>svg]:px-4.5"
                onClick={() => {
                  track('new_dynamic_page_clicked', {
                    source: 'dynamic_pages_header',
                    current_pages_count: dynamicPages?.length || 0,
                  });
                  navigate(`/workspace/${slug}/assistant`);
                }}
              >
                <Sparks className="h-5 w-5" />
                New Page
              </Button>
            </div>
          </div>
        </div>
        <div className="mt-4 flex flex-1 flex-col gap-3">
          <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:justify-start lg:items-center lg:gap-4">
            <div className="flex w-full shrink-0 flex-row flex-wrap items-stretch gap-2 sm:justify-start lg:items-center lg:gap-4">
              <div>
                <DashboardAnalyticsRangeSelector size="normal" variant="text" />
              </div>
              {enableRowSelection && (
                <>
                  <Tooltip
                    open={!Object.keys(rowsSelected).length ? undefined : false}
                  >
                    <TooltipTrigger className="w-fit">
                      <Button
                        className={'font-inter'}
                        variant="outline"
                        size={'sm'}
                        color="white"
                        onClick={() => {
                          track('bulk_update_tags_clicked', {
                            selected_pages_count:
                              Object.keys(rowsSelected).length,
                          });
                          setUpdateDynamicPagesTagsModalOpen(true);
                        }}
                        disabled={!Object.keys(rowsSelected).length}
                      >
                        <Label className="h-4 w-4" />
                        Update Tags
                      </Button>
                    </TooltipTrigger>

                    <TooltipContent>
                      <p>Please select a dynamic page first</p>
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip
                    open={!Object.keys(rowsSelected).length ? undefined : false}
                  >
                    <TooltipTrigger className="w-fit">
                      <Button
                        className={'font-inter'}
                        variant="outline"
                        size={'sm'}
                        color="white"
                        onClick={() => {
                          track('bulk_update_links_clicked', {
                            selected_pages_count:
                              Object.keys(rowsSelected).length,
                          });
                          setUpdateDynamicPagesLinksModalOpen(true);
                        }}
                        disabled={!Object.keys(rowsSelected).length}
                      >
                        <Link className="h-5 w-5 rotate-135" />
                        Update Links
                      </Button>
                    </TooltipTrigger>

                    <TooltipContent>
                      <p>Please select a dynamic page first</p>
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip
                    open={!Object.keys(rowsSelected).length ? undefined : false}
                  >
                    <TooltipTrigger className="w-fit">
                      <Button
                        className={'font-inter'}
                        variant="outline"
                        size={'sm'}
                        color="white"
                        onClick={() => {
                          track('bulk_auto_publish_clicked', {
                            selected_pages_count:
                              Object.keys(rowsSelected).length,
                          });
                          if (!hasActiveSubscription && !isSupportAdmin) {
                            setSubscriptionModalOpen(true);
                            return;
                          }
                          setBulkPublishDynamicPagesModalOpen(true);
                        }}
                        disabled={!Object.keys(rowsSelected).length}
                      >
                        <ArrowUpRight className="h-4 w-4" />
                        Bulk Auto Publish
                      </Button>
                    </TooltipTrigger>

                    <TooltipContent>
                      <p>Please select a dynamic page first</p>
                    </TooltipContent>
                  </Tooltip>
                  <div className="flex-1"></div>
                  <div className="flex items-center gap-2">
                    <Switch
                      size="sm"
                      checked={analyticsType === 'device'}
                      onCheckedChange={(checked) => {
                        track('device_breakdown_toggled', {
                          enabled: checked,
                          source: 'dynamic_pages',
                        });
                        setAnalyticsType(checked ? 'device' : null);
                        if (!checked) {
                          tableRef.current
                            ?.getTableInstance()
                            ?.toggleAllRowsSelected(false);
                          setRowsSelected({});
                        }
                      }}
                    />
                    <span className="font-inter text-sm text-gray-800">
                      Breakdown by Device
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="relative flex-1">
            <DynamicPagesTable
              ref={tableRef}
              items={filteredDynamicPages}
              onRowClick={(dynamicPage) => {
                track('dynamic_page_row_clicked', {
                  dynamic_page_id: dynamicPage.id,
                  dynamic_page_name: dynamicPage.data().name,
                  status: dynamicPage.data().status,
                });
                navigate(`/workspace/${slug}/pages/${dynamicPage.id}`);
              }}
              enableRowSelection={enableRowSelection}
              onRowSelectionChange={(selected) => {
                track('dynamic_pages_selection_changed', {
                  selected_count: selected.length,
                });
                setRowsSelected(
                  Object.fromEntries(selected.map((s) => [s.id, true])),
                );
              }}
              actions={(dynamicPage) => (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size={'icon'}>
                      <MoreVert className="h-4 w-4 text-gray-600" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className={'font-inter'}>
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        track('dynamic_page_duplicate_clicked', {
                          dynamic_page_id: dynamicPage.id,
                          dynamic_page_name: dynamicPage.data().name,
                        });
                        setDuplicateDynamicPageModal({
                          dynamicPage,
                        });
                      }}
                    >
                      <Copy className="h-4 w-4" /> Duplicate
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        track('dynamic_page_open_new_tab_clicked', {
                          dynamic_page_id: dynamicPage.id,
                        });
                        window.open(
                          `/workspace/${slug}/pages/${dynamicPage.id}`,
                          '_blank',
                        );
                      }}
                    >
                      <OpenNewWindow className="h-4 w-4" />
                      Open in new tab
                    </DropdownMenuItem>
                    {dynamicPage.data().status === 'draft' && (
                      <DropdownMenuItem
                        variant="destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          track('dynamic_page_delete_clicked', {
                            dynamic_page_id: dynamicPage.id,
                            dynamic_page_name: dynamicPage.data().name,
                          });
                          setDeleteDynamicPageModal({
                            dynamicPage,
                          });
                        }}
                      >
                        <Trash className="h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            />
          </div>
        </div>
      </div>
      <SubscriptionRequiredModal
        open={subscriptionModalOpen}
        onClose={() => setSubscriptionModalOpen(false)}
      />
    </>
  );
}
